/**
 * Date/time formatting for the field team in Berlin.
 *
 * Backend values arrive in three shapes: `timestamptz` (ISO string in UTC), `date` (`YYYY-MM-DD`)
 * and `time` (`HH:MM[:SS]`). Timestamps are converted to `Europe/Berlin`; calendar dates and wall
 * clock times are already Berlin-local and must be rendered as-is instead of being shifted again.
 */

export const BERLIN_TZ = 'Europe/Berlin';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const TIME_ONLY = /^\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

type DateInput = string | number | Date | null | undefined;

function berlinFormat(value: Date, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('de-DE', { timeZone: BERLIN_TZ, ...options }).format(value);
}

function parseCalendarDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12));
}

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);
  if (DATE_ONLY.test(value)) return parseCalendarDate(value);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Calendar dates are timezone-free, so they must not be converted. */
function isCalendarDate(value: DateInput): boolean {
  return typeof value === 'string' && DATE_ONLY.test(value);
}

/** `01.08.2026`, empty string for missing values. */
export function formatDate(value: DateInput, fallback = ''): string {
  const date = toDate(value);
  if (!date) return fallback;
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  return isCalendarDate(value)
    ? new Intl.DateTimeFormat('de-DE', { ...options, timeZone: 'UTC' }).format(date)
    : berlinFormat(date, options);
}

/** `Sa., 01. Aug.` for compact list rows. */
export function formatDateShort(value: DateInput, fallback = ''): string {
  const date = toDate(value);
  if (!date) return fallback;
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  };
  return isCalendarDate(value)
    ? new Intl.DateTimeFormat('de-DE', { ...options, timeZone: 'UTC' }).format(date)
    : berlinFormat(date, options);
}

/** `16:01`. Accepts `time` columns and full timestamps. */
export function formatTime(value: DateInput, fallback = ''): string {
  if (typeof value === 'string' && TIME_ONLY.test(value)) {
    return value.substring(0, 5);
  }
  const date = toDate(value);
  if (!date || isCalendarDate(value)) return fallback;
  return berlinFormat(date, { hour: '2-digit', minute: '2-digit' });
}

/** `01.08.2026, 16:01`. */
export function formatDateTime(value: DateInput, fallback = ''): string {
  const date = toDate(value);
  if (!date) return fallback;
  if (isCalendarDate(value)) return formatDate(value, fallback);
  return berlinFormat(date, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Berlin-local today as `YYYY-MM-DD`, for prefilling `<input type="date">`. */
export function berlinTodayIso(at: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BERLIN_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
}

/**
 * Legacy dates imported with a zero timestamp show up as 1970 and should be treated as "no date".
 */
export function hasRealDate(value: DateInput): boolean {
  const date = toDate(value);
  return date !== null && date.getUTCFullYear() > 1970;
}
